import { useCallback, useEffect, useRef, useState } from 'react';
import type { RemotePeer, UseZCallResult } from './useZCallWebRTC';
import { requestSfuToken } from '@/lib/zcallSfu';

// ─────────────────────────────────────────────────────────────
// ZCall — transport SFU (serveur média) pour les LIVES / grandes visio.
//
// Alternative au maillage P2P : chaque participant n'envoie qu'UN flux vers le
// serveur média, qui le redistribue → des dizaines/centaines de participants.
// Le SDK client (LiveKit) est chargé « à la demande » (import dynamique) : il
// n'est téléchargé QUE si le SFU est activé, et l'app reste légère sinon.
//
// Le hook expose exactement la même interface que le moteur maillage, ce qui
// permet de basculer entre les deux sans changer la salle ni les composants.
// ─────────────────────────────────────────────────────────────

type LkRoom = import('livekit-client').Room;
type LkLocalParticipant = import('livekit-client').LocalParticipant;

interface LiveKitModule {
  Room: typeof import('livekit-client').Room;
  RoomEvent: typeof import('livekit-client').RoomEvent;
}

/** Récupère la piste média (MediaStreamTrack) d'une piste LiveKit, sans type strict. */
function trackToMediaStreamTrack(track: unknown): MediaStreamTrack | null {
  const mst = (track as { mediaStreamTrack?: MediaStreamTrack } | null | undefined)?.mediaStreamTrack;
  return mst ?? null;
}

function newIdentity(): string {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return `u-${crypto.randomUUID().slice(0, 12)}`;
    }
  } catch {
    /* repli ci-dessous */
  }
  return `u-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function useZCallSfu(
  roomId: string,
  userName: string,
  active: boolean,
  serverUrl: string,
  publish = true,
): UseZCallResult {
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remotes, setRemotes] = useState<RemotePeer[]>([]);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [screenSharing, setScreenSharing] = useState(false);
  const [status, setStatus] = useState<'idle' | 'connecting' | 'connected' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  const identityRef = useRef<string>('');
  const roomRef = useRef<LkRoom | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteStreamsRef = useRef<Map<string, MediaStream>>(new Map());
  const remoteNamesRef = useRef<Map<string, string>>(new Map());

  const pushRemotes = useCallback(() => {
    const list: RemotePeer[] = [];
    remoteStreamsRef.current.forEach((stream, id) => {
      list.push({ id, name: remoteNamesRef.current.get(id) || 'Participant', stream });
    });
    setRemotes(list);
  }, []);

  const rebuildLocalStream = useCallback((participant: LkLocalParticipant) => {
    const ms = new MediaStream();
    participant.trackPublications.forEach((pub) => {
      const t = trackToMediaStreamTrack(pub.track);
      if (t) ms.addTrack(t);
    });
    localStreamRef.current = ms;
    setLocalStream(ms);
  }, []);

  const toggleMic = useCallback(() => {
    const room = roomRef.current;
    if (!room) return;
    setMicOn((v) => {
      const next = !v;
      void room.localParticipant.setMicrophoneEnabled(next).catch(() => { /* ignoré */ });
      return next;
    });
  }, []);

  const setMicEnabled = useCallback((on: boolean) => {
    const room = roomRef.current;
    setMicOn(on);
    if (!room) return;
    void room.localParticipant.setMicrophoneEnabled(on).catch(() => { /* ignoré */ });
  }, []);

  const toggleCam = useCallback(() => {
    const room = roomRef.current;
    if (!room) return;
    setCamOn((v) => {
      const next = !v;
      void room.localParticipant.setCameraEnabled(next).catch(() => { /* ignoré */ });
      return next;
    });
  }, []);

  const setCamEnabled = useCallback((on: boolean) => {
    const room = roomRef.current;
    setCamOn(on);
    if (!room) return;
    void room.localParticipant.setCameraEnabled(on).catch(() => { /* ignoré */ });
  }, []);

  const startScreenShare = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    try {
      await room.localParticipant.setScreenShareEnabled(true);
      setScreenSharing(true);
    } catch {
      /* partage refusé */
    }
  }, []);

  const stopScreenShare = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    try {
      await room.localParticipant.setScreenShareEnabled(false);
    } catch {
      /* ignoré */
    }
    setScreenSharing(false);
  }, []);

  useEffect(() => {
    if (!active || !roomId) return undefined;
    let cancelled = false;
    if (!identityRef.current) identityRef.current = newIdentity();
    let roomToDisconnect: LkRoom | null = null;

    const start = async () => {
      setStatus('connecting');
      setError(null);

      const session = await requestSfuToken(roomId, identityRef.current, userName || 'Participant');
      if (cancelled) return;
      if (!session) {
        setStatus('error');
        setError('Serveur média (SFU) indisponible — bascule en connexion directe.');
        return;
      }

      let LK: LiveKitModule;
      try {
        LK = (await import('livekit-client')) as unknown as LiveKitModule;
      } catch {
        if (!cancelled) {
          setStatus('error');
          setError('Module de visioconférence (SFU) introuvable.');
        }
        return;
      }
      if (cancelled) return;

      const room = new LK.Room({ adaptiveStream: true, dynacast: true });
      roomRef.current = room;
      roomToDisconnect = room;

      room
        .on(LK.RoomEvent.TrackSubscribed, (track, _pub, participant) => {
          const id = participant.identity;
          remoteNamesRef.current.set(id, participant.name || participant.identity || 'Participant');
          let ms = remoteStreamsRef.current.get(id);
          if (!ms) {
            ms = new MediaStream();
            remoteStreamsRef.current.set(id, ms);
          }
          const mst = trackToMediaStreamTrack(track);
          if (mst && !ms.getTracks().some((t) => t === mst)) ms.addTrack(mst);
          pushRemotes();
        })
        .on(LK.RoomEvent.TrackUnsubscribed, (track, _pub, participant) => {
          const ms = remoteStreamsRef.current.get(participant.identity);
          const mst = trackToMediaStreamTrack(track);
          if (ms && mst) {
            try {
              ms.removeTrack(mst);
            } catch {
              /* ignoré */
            }
          }
          pushRemotes();
        })
        .on(LK.RoomEvent.ParticipantConnected, (participant) => {
          remoteNamesRef.current.set(participant.identity, participant.name || participant.identity);
        })
        .on(LK.RoomEvent.ParticipantDisconnected, (participant) => {
          remoteStreamsRef.current.delete(participant.identity);
          remoteNamesRef.current.delete(participant.identity);
          pushRemotes();
        })
        .on(LK.RoomEvent.LocalTrackPublished, () => { rebuildLocalStream(room.localParticipant); })
        .on(LK.RoomEvent.LocalTrackUnpublished, () => { rebuildLocalStream(room.localParticipant); })
        .on(LK.RoomEvent.Disconnected, () => {
          if (!cancelled) {
            setStatus('error');
            setError('Connexion au serveur média perdue.');
          }
        });

      try {
        await room.connect(session.url || serverUrl, session.token);
      } catch {
        if (!cancelled) {
          setStatus('error');
          setError('Échec de connexion au serveur média.');
        }
        return;
      }
      if (cancelled) return;

      setStatus('connected');
      if (publish) {
        try {
          await room.localParticipant.setCameraEnabled(true);
        } catch {
          if (!cancelled) setCamOn(false);
        }
        try {
          await room.localParticipant.setMicrophoneEnabled(true);
        } catch {
          if (!cancelled) setMicOn(false);
        }
        if (!cancelled) rebuildLocalStream(room.localParticipant);
      } else {
        // Spectateur (live) : aucune publication locale.
        setCamOn(false);
        setMicOn(false);
      }
    };

    void start();

    return () => {
      cancelled = true;
      remoteStreamsRef.current.clear();
      remoteNamesRef.current.clear();
      localStreamRef.current = null;
      roomRef.current = null;
      setLocalStream(null);
      setRemotes([]);
      setScreenSharing(false);
      setMicOn(true);
      setCamOn(true);
      if (roomToDisconnect) {
        try {
          void roomToDisconnect.disconnect();
        } catch {
          /* ignoré */
        }
      }
    };
  }, [active, roomId, userName, serverUrl, publish, pushRemotes, rebuildLocalStream]);

  return {
    clientId: identityRef.current,
    localStream,
    remotes,
    micOn,
    camOn,
    screenSharing,
    status,
    error,
    toggleMic,
    toggleCam,
    setMicEnabled,
    setCamEnabled,
    startScreenShare,
    stopScreenShare,
  };
}